import { jsPDF } from 'jspdf'

// Re-uses the exact same drawObject logic as Whiteboard.jsx so the
// exported PDF looks identical to what was on screen.
function drawObjectOnCtx(ctx, obj) {
  ctx.strokeStyle = obj.color
  ctx.fillStyle = obj.color
  ctx.lineWidth = obj.strokeWidth
  ctx.lineCap = 'round'
  ctx.lineJoin = 'round'
  const g = obj.geometry

  if (g.kind === 'freehand') {
    if (g.points.length === 0) return
    ctx.beginPath()
    ctx.moveTo(g.points[0].x, g.points[0].y)
    for (let i = 1; i < g.points.length; i++) ctx.lineTo(g.points[i].x, g.points[i].y)
    ctx.stroke()
    return
  }

  if (g.kind === 'shape') {
    const { start, end, filled } = g
    const x = Math.min(start.x, end.x), y = Math.min(start.y, end.y)
    const w = Math.abs(end.x - start.x), h = Math.abs(end.y - start.y)
    if (obj.tool === 'rect') {
      if (filled) ctx.fillRect(x, y, w, h); else ctx.strokeRect(x, y, w, h)
    } else if (obj.tool === 'circle') {
      ctx.beginPath()
      ctx.ellipse(x + w / 2, y + h / 2, w / 2, h / 2, 0, 0, Math.PI * 2)
      if (filled) ctx.fill(); else ctx.stroke()
    } else if (obj.tool === 'line') {
      ctx.beginPath(); ctx.moveTo(start.x, start.y); ctx.lineTo(end.x, end.y); ctx.stroke()
    } else if (obj.tool === 'arrow') {
      ctx.beginPath(); ctx.moveTo(start.x, start.y); ctx.lineTo(end.x, end.y); ctx.stroke()
      const angle = Math.atan2(end.y - start.y, end.x - start.x)
      const headLen = 10 + obj.strokeWidth * 2
      const headAngle = Math.PI / 7
      ctx.beginPath()
      ctx.moveTo(end.x, end.y)
      ctx.lineTo(end.x - headLen * Math.cos(angle - headAngle), end.y - headLen * Math.sin(angle - headAngle))
      ctx.moveTo(end.x, end.y)
      ctx.lineTo(end.x - headLen * Math.cos(angle + headAngle), end.y - headLen * Math.sin(angle + headAngle))
      ctx.stroke()
    }
    return
  }

  if (g.kind === 'text') {
    const size = 12 + obj.strokeWidth * 2
    ctx.font = `${size}px ${g.fontFamily === 'serif' ? 'Georgia, serif' : g.fontFamily === 'mono' ? 'monospace' : 'system-ui, sans-serif'}`
    ctx.textBaseline = 'top'
    ctx.fillText(g.content, g.anchor.x, g.anchor.y)
  }
}

// Renders one page's objects (and optional background image) to an
// offscreen canvas and returns a PNG data URL.
//
// BUG FIX — ASYNC BACKGROUND IMAGES: the original version was
// synchronous and didn't accept or draw backgroundImageUrl at all,
// so any page that had a PDF worksheet background exported as a
// plain white page. This version loads the background first (if
// any), draws it to fill the canvas, then draws the strokes on top.
// It's async because Image.onload is async — the caller must await it.
const EXPORT_WIDTH = 1600
const EXPORT_HEIGHT = 1000

async function renderPageToDataUrl(pageObjects, backgroundImageUrl) {
  const canvas = document.createElement('canvas')
  canvas.width = EXPORT_WIDTH
  canvas.height = EXPORT_HEIGHT
  const ctx = canvas.getContext('2d')

  ctx.fillStyle = '#ffffff'
  ctx.fillRect(0, 0, EXPORT_WIDTH, EXPORT_HEIGHT)

  // BUG FIX: draw the worksheet background before strokes.
  // crossOrigin must be set before .src to avoid a tainted-canvas
  // error when the image comes from a different origin (Cloud Storage).
  if (backgroundImageUrl) {
    await new Promise((resolve) => {
      const img = new Image()
      img.crossOrigin = 'anonymous'
      img.onload = () => {
        ctx.drawImage(img, 0, 0, EXPORT_WIDTH, EXPORT_HEIGHT)
        resolve()
      }
      img.onerror = () => {
        // Background failed to load — keep the white fill and
        // continue so at least the strokes are exported.
        console.warn('[exportBoard] Failed to load background image, exporting without it:', backgroundImageUrl)
        resolve()
      }
      img.src = backgroundImageUrl
    })
  }

  for (const obj of pageObjects) {
    if (obj.__preview) continue
    drawObjectOnCtx(ctx, obj)
  }

  try {
    // BUG FIX — SILENT PNG FALLBACK BLOWING UP FILE SIZE: this used to
    // assume canvas.toDataURL('image/jpeg', 0.85) always produces a
    // JPEG and hard-coded 'JPEG' in the addImage() call below. But
    // per the HTML5 canvas spec, if the underlying engine doesn't
    // support encoding to the requested MIME type, toDataURL MUST
    // silently fall back to PNG — no error, no warning, just a
    // 'data:image/png;...' URL despite asking for JPEG. Some mobile
    // WebView engines do exactly this. When that happened here, we
    // were still telling jsPDF "this is a JPEG", which caused it to
    // fail to parse the real (PNG) bytes as JPEG and fall back to
    // embedding an uncompressed raw bitmap — 1600×1000×3 bytes =
    // 4,800,000, matching almost exactly the 4,803,497-byte export
    // that was observed. Reading the ACTUAL mime prefix off the
    // returned data URL and passing THAT to jsPDF (instead of
    // assuming) fixes this regardless of which format the engine
    // actually produced.
    const dataUrl = canvas.toDataURL('image/jpeg', 0.85)
    const format = dataUrl.startsWith('data:image/jpeg') ? 'JPEG' : 'PNG'
    return { dataUrl, format }
  } catch (err) {
    // BUG FIX — TAINTED CANVAS: if backgroundImageUrl's server didn't
    // send the right Access-Control-Allow-Origin header, the browser
    // still LOADS the image (img.onload fires, not onerror) but marks
    // the canvas "tainted" the moment drawImage() paints it — and
    // toDataURL() on a tainted canvas throws a SecurityError. Without
    // this catch, that error propagated up as an opaque
    // "SecurityError: Failed to execute 'toDataURL'" with no
    // indication of WHICH page or WHY, making it look identical to
    // any other export failure. Re-throwing with the actual page and
    // URL means the real cause shows up in Whiteboard.jsx's export
    // failure log instead of forcing a guess.
    const wrapped = new Error(
      `Failed to export page image (tainted canvas — likely a CORS issue with the background image at ${backgroundImageUrl}): ${err.message}`
    )
    wrapped.cause = err
    throw wrapped
  }
}

// Builds a single PDF with one page per whiteboard page and
// returns it as a Blob ready to upload. Header shows task title,
// other party name, date/time, and page numbers.
//
// BUG FIX: this is now async because renderPageToDataUrl is async
// (image loading). The caller in Whiteboard.jsx must await it —
// handleExport already uses await, so no change needed there.
export async function exportWhiteboardToPdf(pages, objects, taskTitle, otherPartyName) {
  const PAGE_WIDTH  = EXPORT_WIDTH / 2
  const PAGE_HEIGHT = EXPORT_HEIGHT / 2
  const HEADER_HEIGHT = 44

  const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: [PAGE_WIDTH, PAGE_HEIGHT] })

  const now = new Date()
  const dateTimeStr = now.toLocaleString('en-CA', {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit',
  })

  for (let i = 0; i < pages.length; i++) {
    const page = pages[i]
    const pageObjects = objects.filter((o) => o.pageId === page.id)
    // BUG FIX: pass backgroundImageUrl so worksheet pages render correctly.
    const { dataUrl, format } = await renderPageToDataUrl(pageObjects, page.backgroundImageUrl || null)
    if (i > 0) doc.addPage([PAGE_WIDTH, PAGE_HEIGHT], 'landscape')

    // Header background
    doc.setFillColor(248, 248, 248)
    doc.rect(0, 0, PAGE_WIDTH, HEADER_HEIGHT, 'F')

    // Task title — bold, dark
    doc.setFontSize(12)
    doc.setFont('helvetica', 'bold')
    doc.setTextColor(30, 30, 30)
    doc.text(taskTitle || 'Whiteboard Notes', 14, 16)

    // Other party name + date — normal weight, grey
    doc.setFontSize(9)
    doc.setFont('helvetica', 'normal')
    doc.setTextColor(100, 100, 100)
    const subtitle = [otherPartyName, dateTimeStr].filter(Boolean).join('   ·   ')
    doc.text(subtitle, 14, 30)

    // Page number top-right (only shown when multi-page)
    if (pages.length > 1) {
      doc.setFontSize(8)
      doc.setTextColor(150, 150, 150)
      doc.text(`Page ${i + 1} of ${pages.length}`, PAGE_WIDTH - 14, 16, { align: 'right' })
    }

    // Separator line
    doc.setDrawColor(220, 220, 220)
    doc.setLineWidth(0.5)
    doc.line(0, HEADER_HEIGHT, PAGE_WIDTH, HEADER_HEIGHT)

    // Board content
    // Format is whatever the canvas actually produced (see renderPageToDataUrl) —
    // never assumed, since some engines silently fall back to PNG.
    doc.addImage(dataUrl, format, 0, HEADER_HEIGHT, PAGE_WIDTH, PAGE_HEIGHT - HEADER_HEIGHT)
  }

  const rawBlob = doc.output('blob')
  // BUG FIX — BLOB MIME TYPE: jsPDF's output('blob') should already
  // tag the blob as 'application/pdf', but if it ever comes back
  // without a type (some jsPDF/browser combinations have been known
  // to produce an untyped blob), the multipart request in api.js
  // sends it with no reliable Content-Type for that part, and the
  // server's multer fileFilter rejects it outright with "Only PDF
  // files are accepted" — a 400 that looks identical to every other
  // export failure until you check the response body. Force the
  // correct type here so this failure mode can never happen silently.
  return rawBlob.type === 'application/pdf'
    ? rawBlob
    : new Blob([rawBlob], { type: 'application/pdf' })
}